import { useEffect, useState, useMemo, useRef } from 'react'
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Icon,
  Icons,
  Modal,
  Popover,
  Select,
  Switch,
  Textfield,
  Button
} from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  lighten,
  ListItemIcon,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { frequencyEnum } from 'models/Invoice'
import { constructorProducts, Products } from 'models/Plans'
import Image from 'next/image'
import { MAX_CYCLES_CHARGE } from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/FrequencyControls'
import FormProducts from 'templates/v2/Products/Form/FormProducts'
import { getPlanDueDisplay } from 'templates/v2/Products/utils/dueDisplay'

import { ProductAutocomplete } from 'components/AutocompleteProducts/AutocompleteProducts'
import CanLogical from 'components/CanLogical'
import { GradientOpenWithIcon } from 'components/GradientIcon/GradientIcon'
import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'
import ChipTag, { ChipTagData } from 'components/Tag/ChipTag'
import TagsPopover from 'components/TagsPopover'
import Upgrade from 'components/Upgrade'

import { currency, formatMoney } from 'utils'
import { onlyNumber, priceConverter } from 'utils/FormatMoney'
import {
  cycleIntervalBetween,
  describeFrequency,
  fitsInComposition,
  FREQUENCY_ENUM_TO_NAME,
  FrequencyUnit,
  ItemCadence,
  pairToPreset,
  presetToPair
} from 'utils/frequency'

import { theme } from 'styles/theme'

import { ItemRowProps } from './ItemRow.interface'

// Bonificação do item: mesma nomenclatura do backend (Billing::BonusPolicy).
type ItemBonusPolicy = 'no_bonus' | 'fixed' | 'percentage'

const ITEM_BONUS_POLICY_OPTIONS = [
  { label: 'Valor', value: 'fixed' },
  { label: 'Porcentagem', value: 'percentage' }
]

// "Por quantos ciclos?" — mesmas opções da bonificação (ver Discounts):
// Todos = 0 (até o fim), 1–12 diretos, Outros = 13 (input livre).
const ITEM_CYCLES_OPTIONS = [
  { label: 'Todos', value: 0 },
  ...Array.from({ length: 12 }, (_, i) => ({
    label: String(i + 1),
    value: i + 1
  })),
  { label: 'Outros', value: 13 }
]

// Padrão de accordion do sistema (barra verde à esquerda) — ver conta/CLAUDE.md
const ADVANCED_ACCORDION_SX = {
  border: '1px solid #E8EBE8',
  borderLeft: '3px solid',
  borderLeftColor: 'primary.main',
  borderRadius: '8px !important',
  backgroundColor: '#FAFBFA',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: 0,
    backgroundColor: '#fff'
  }
}

// Chips de tag do item são um espelho compacto do que está no popover —
// menores que o ChipTag padrão das telas de detalhe, para não competir com
// os campos do item.
const COMPACT_CHIP_TAG_SX = {
  height: '22px',
  fontSize: '11px',
  '& .MuiChip-label': { px: '8px' },
  '& .MuiChip-deleteIcon': { fontSize: '14px', ml: '-2px', mr: '5px' }
}

// Altura do Textfield da linha do item. As células sem label (total, tags, remover)
// se alinham por ela em vez de descerem com padding fixo — assim nenhuma célula fica
// mais alta que os inputs e a linha inteira mantém a mesma base.
const FIELD_ROW_HEIGHT = '44px'

const ItemRow = <T extends FieldValues>({
  field,
  index,
  control,
  watch,
  setValue,
  errors,
  products,
  hasFetched,
  itemsFields,
  removeItem,
  updateDescriptionWithItems,
  refetchProducts,
  isPaymentLink = false,
  isRecurrence = false,
  currentCycle
}: ItemRowProps<T>) => {
  const canManageProducts = CanLogical({ permission: 'products:manage' })
  // Bonificação é recurso de plano: mesma capability e mesmo gate da bonificação
  // global (AdvancedOptions). Sem acesso o switch fica travado e o selo de upgrade
  // aparece; uma bonificação já gravada continua visível, só não é editável.
  //
  // `capabilities` nasce null e só é preenchido quando /v1/merchant_capabilities
  // responde (o claim saiu do JWT no SC-10878). Enquanto isso o switch fica
  // travado: liberar por padrão deixaria um merchant sem o plano ligar a
  // bonificação na janela de carregamento.
  const { capabilities } = useAuthContext()
  const bonusAccess = capabilities?.['billing.bonus']
  const canUseBonus = Boolean(bonusAccess?.has_access)

  const ADD_PLAN_VALUE = '__add_new_plan__'
  const LOADING_VALUE = '__loading__'

  const optionsWithAddButton = [
    // Só mostrar botão "Adicionar plano" para quem tem products:manage
    ...(canManageProducts
      ? [
          {
            label: '',
            value: ADD_PLAN_VALUE,
            product: { name: '', uuid: ADD_PLAN_VALUE }
          }
        ]
      : []),
    // Adicionar opção de loading quando estiver carregando
    ...(!hasFetched
      ? [
          {
            label: 'Carregando...',
            value: LOADING_VALUE,
            product: { name: 'Carregando...', uuid: LOADING_VALUE }
          }
        ]
      : []),
    ...products
  ]

  const isMobile = useMediaQuery('(max-width: 960px )')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [currentProduct, setCurrentProduct] = useState<ProductAutocomplete>()
  const [productToBeEdited, setProductToBeEdited] = useState<
    Products | undefined
  >()
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false)
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false)
  const [isLoadingProduct, setIsLoadingProduct] = useState<boolean>(true)
  const [isUpdatingProduct, setIsUpdatingProduct] = useState<boolean>(false)
  const [autocompleteInputValue, setAutocompleteInputValue] = useState(
    () => (watch(`items.${index}.typedValue` as Path<T>) as string) || ''
  )

  // ----- Janela de ciclos do item (só recorrência) -----

  // Piso de ciclo só se aplica a item NOVO: um item incluído numa recorrência
  // em andamento não pode começar em ciclo já decorrido. Item já persistido
  // (tem uuid) mantém o start_cycle original, mesmo que anterior ao ciclo atual
  // — o backend também só pisa itens novos (V3::RecurrencesController#update,
  // ramos de criação). Sem essa distinção, editar uma recorrência acusava erro
  // num item legítimo configurado, p.ex., para os ciclos 1–5.
  const isPersistedItem = !!field?.uuid
  const minStartCycle =
    isRecurrence && !isPersistedItem ? Number(currentCycle || 0) + 1 : 1

  const itemTags = watch(`items.${index}.tags` as Path<T>) as
    | ChipTagData[]
    | undefined

  // Mesma condição que decide entre o card do plano e o autocomplete livre:
  // define onde os chips de tag são renderizados (dentro do card ou abaixo da linha).
  const hasProductSelected =
    !!watch(`items.${index}.product` as Path<T>) &&
    watch(`items.${index}.product` as Path<T>)?.value !== 'custom'
  const itemStartCycle = Number(
    watch(`items.${index}.start_cycle` as Path<T>) ?? 1
  )
  const itemTotalCycles = Number(
    watch(`items.${index}.total_cycles` as Path<T>) ?? 0
  )
  // Cadência do item: de quantos em quantos ciclos ele é cobrado. Vem da frequência do
  // produto e NÃO é editável — o merchant só vê o efeito no texto-guia.
  const itemCycleInterval = Math.max(
    Number(watch(`items.${index}.cycle_interval` as Path<T>) ?? 1),
    1
  )

  // Rateio proporcional por item (`items.N.prorate`) é editado no ProrateItemsSelector,
  // logo abaixo do switch "Cobrar proporcional" — lá o merchant vê a composição inteira e
  // o que fica de fora. Aqui não há controle: dois switches gravando o mesmo campo já
  // causou bug de estado nesta feature. Default true continua valendo (campo ausente = rateia).

  // total_cycles === 0 significa "até o fim da recorrência". A janela só conta
  // como configurada quando o item não cobre todos os ciclos ainda disponíveis
  // — começar em minStartCycle é o piso natural, não uma restrição do merchant.
  const hasCycleWindowConfigured =
    itemStartCycle > minStartCycle || itemTotalCycles > 0

  const [expandedAdvanced, setExpandedAdvanced] = useState(
    () => hasCycleWindowConfigured
  )
  const [hasCycleWindow, setHasCycleWindow] = useState(
    () => hasCycleWindowConfigured
  )

  // Rascunho do "A partir de qual ciclo?": null = seguir o form. Enquanto o merchant
  // digita, o input precisa poder ficar vazio — aplicar o piso a cada tecla reescrevia
  // o valor no mesmo render e tornava impossível apagar o dígito. O form só recebe
  // valor válido: o piso entra no blur.
  const [startCycleDraft, setStartCycleDraft] = useState<string | null>(null)

  // "Por quantos ciclos?" segue o Select da bonificação: 0 = Todos (até o fim),
  // 1–12 diretos, 13 = Outros (input livre). O modo "Outros" é local; o valor
  // real vive sempre em total_cycles.
  const [cyclesIsCustom, setCyclesIsCustom] = useState(
    () => itemTotalCycles > 12
  )
  const cyclesSelectValue =
    cyclesIsCustom || itemTotalCycles > 12 ? 13 : itemTotalCycles

  // Bonificação do item: valor único (sem faixas), em R$ ou %, abatimento INCONDICIONAL
  // que reduz o amount_billed da cobrança (vale em qualquer prazo de pagamento).
  const itemBonusPolicy = (watch(`items.${index}.bonus_policy` as Path<T>) ??
    'no_bonus') as ItemBonusPolicy
  const itemBonusAmount = watch(`items.${index}.bonus_amount` as Path<T>) as
    | string
    | number
    | undefined

  const [hasItemBonus, setHasItemBonus] = useState(
    () => itemBonusPolicy !== 'no_bonus'
  )

  // Janela da bonificação do item (SC-11169): em quais ciclos da recorrência ela vale.
  // Mesma regra e mesmos controles da bonificação geral: ciclo inicial + "Por quantos
  // ciclos?" (0 = Todos, 1–12, Outros). Só existe em recorrência; na cobrança avulsa a
  // bonificação vale na própria cobrança.
  const itemBonusStartCycle =
    Number(watch(`items.${index}.bonus_start_cycle` as Path<T>)) || 1
  const itemBonusTotalCycles =
    Number(watch(`items.${index}.bonus_total_cycles` as Path<T>)) || 0
  const [bonusStartCycleDraft, setBonusStartCycleDraft] = useState<
    string | null
  >(null)
  const [bonusCyclesIsCustom, setBonusCyclesIsCustom] = useState(
    () => itemBonusTotalCycles > 12
  )
  const bonusCyclesSelectValue =
    bonusCyclesIsCustom || itemBonusTotalCycles > 12 ? 13 : itemBonusTotalCycles

  // Na edição o form é preenchido depois da montagem da linha, então o estado inicial
  // dos switches nasce "desligado" e a seção ficaria fechada escondendo uma bonificação/janela
  // que existem. Só liga — nunca desliga — para não brigar com o toggle do merchant.
  useEffect(() => {
    if (itemBonusPolicy !== 'no_bonus') {
      setHasItemBonus(true)
      setExpandedAdvanced(true)
    }
  }, [itemBonusPolicy])

  useEffect(() => {
    if (hasCycleWindowConfigured) {
      setHasCycleWindow(true)
      setExpandedAdvanced(true)
    }
  }, [hasCycleWindowConfigured])

  // Escolher um plano dispara uma cascata assíncrona: `configurePlan` grava a janela no
  // item e só depois chama setIsUnique, que pode virar a cobrança para Única e desabilitar
  // o card de ciclos. Medido no navegador, a sequência é:
  //   1) isRecurrence=true,  total_cycles=1  → card ligado e ativo
  //   2) isRecurrence=false, total_cycles=1  → card desabilitado
  // Cobrir só o passo 2 (estado incoerente) ainda deixava o passo 1 aparecer. A trava
  // começa, então, na própria escrita da janela e só abre quando o estado para de mudar.
  const [isSettlingPlan, setIsSettlingPlan] = useState(false)
  const planSettleSignature = `${isRecurrence}:${itemTotalCycles}:${itemStartCycle}:${itemCycleInterval}`
  const lastSettleSignature = useRef(planSettleSignature)

  useEffect(() => {
    if (lastSettleSignature.current === planSettleSignature) return undefined

    lastSettleSignature.current = planSettleSignature
    setIsSettlingPlan(true)

    // Sem nova mudança em ~180ms, a cascata terminou e o estado final é o que fica.
    // O tempo não coordena a troca (isso falhou antes) — só detecta a quietude.
    const timeout = setTimeout(() => setIsSettlingPlan(false), 180)
    return () => clearTimeout(timeout)
  }, [planSettleSignature])

  const handleToggleItemBonus = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    if (!canUseBonus) return

    setHasItemBonus(isExpanded)

    setValue(
      `items.${index}.bonus_policy` as Path<T>,
      (isExpanded ? 'fixed' : 'no_bonus') as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )

    // Desligar limpa o valor: o backend ignora bonus_amount quando a política
    // é no_bonus, mas deixar resíduo no form confunde ao religar.
    if (!isExpanded) {
      setValue(
        `items.${index}.bonus_amount` as Path<T>,
        0 as PathValue<T, Path<T>>,
        { shouldDirty: true }
      )
      resetItemBonusWindow()
    }
  }

  const resetItemBonusWindow = () => {
    setValue(
      `items.${index}.bonus_start_cycle` as Path<T>,
      1 as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )
    setValue(
      `items.${index}.bonus_total_cycles` as Path<T>,
      0 as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )
    setBonusStartCycleDraft(null)
    setBonusCyclesIsCustom(false)
  }

  // Mesmo comportamento do "A partir de qual ciclo?" da janela do item: o input pode ficar
  // vazio enquanto o merchant digita, e o piso (ciclo 1) entra só no blur.
  const handleChangeBonusStartCycle = (rawValue: string) => {
    const input = onlyNumber(rawValue)
    if (Number(input) > MAX_CYCLES_CHARGE) return

    setBonusStartCycleDraft(input)
    if (!input) return

    setValue(
      `items.${index}.bonus_start_cycle` as Path<T>,
      parseInt(input, 10) as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )
  }

  const handleBlurBonusStartCycle = () => {
    const parsed = parseInt(bonusStartCycleDraft ?? '', 10)

    if (bonusStartCycleDraft !== null && (Number.isNaN(parsed) || parsed < 1)) {
      setValue(
        `items.${index}.bonus_start_cycle` as Path<T>,
        1 as PathValue<T, Path<T>>,
        { shouldDirty: true }
      )
    }

    setBonusStartCycleDraft(null)
  }

  const handleChangeBonusCyclesSelect = (value: number) => {
    if (value === 13) {
      // "Outros": mantém a quantidade atual e revela o input livre
      setBonusCyclesIsCustom(true)
      return
    }
    setBonusCyclesIsCustom(false)
    setValue(
      `items.${index}.bonus_total_cycles` as Path<T>,
      value as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )
  }

  const handleChangeBonusCyclesCustom = (rawValue: string) => {
    const res = onlyNumber(rawValue)
    if (Number(res) <= MAX_CYCLES_CHARGE) {
      setValue(
        `items.${index}.bonus_total_cycles` as Path<T>,
        (Number(res) || 0) as PathValue<T, Path<T>>,
        { shouldDirty: true }
      )
    }
  }

  // Texto da janela da bonificação para o helper ("nos ciclos 3 a 8"). Vazio no default.
  const bonusWindowLabel = (() => {
    if (!isRecurrence) return ''
    const start = Math.max(itemBonusStartCycle, 1)
    const total = Math.max(itemBonusTotalCycles, 0)

    if (start <= 1 && total === 0) return ''
    if (total === 0) return `a partir do ciclo ${start}`
    if (total === 1) return `no ciclo ${start}`
    return `nos ciclos ${start} a ${start + total - 1}`
  })()

  // Trocar R$ <-> % zera o valor: 10 reais e 10% não são a mesma coisa, manter o
  // número ao trocar a unidade viraria uma bonificação errada sem o merchant perceber.
  const handleChangeBonusPolicy = (policy: ItemBonusPolicy) => {
    setValue(
      `items.${index}.bonus_policy` as Path<T>,
      policy as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )
    setValue(
      `items.${index}.bonus_amount` as Path<T>,
      0 as PathValue<T, Path<T>>,
      { shouldDirty: true }
    )
  }

  // Valor efetivo da bonificação, espelhando BillingItem#bonus_value (inclusive o
  // teto no valor do item) para a tela não prometer mais do que o backend concede.
  //
  // O campo em R$ usa máscara money, então o form guarda centavos ("2000" = R$ 20,00)
  // — priceConverter é o mesmo conversor que o total dos itens usa. Já o percentual
  // usa onlyNumber e guarda o número puro ("10" = 10%), sem conversão.
  const itemBonusValue = (() => {
    const total = Number(watch(`items.${index}.total` as Path<T>)) || 0
    if (itemBonusPolicy === 'no_bonus') return 0

    const raw =
      itemBonusPolicy === 'percentage'
        ? (total * (Number(itemBonusAmount) || 0)) / 100
        : priceConverter(itemBonusAmount)

    if (raw <= 0) return 0
    return Math.min(raw, total)
  })()

  // Remover pelo chip escreve no mesmo array que o TagsPopover controla, então
  // o badge e a lista dentro do popover acompanham sem estado extra.
  const handleRemoveItemTag = (tagId: string) => {
    setValue(
      `items.${index}.tags` as Path<T>,
      (itemTags ?? []).filter((tag) => tag.id !== tagId) as PathValue<
        T,
        Path<T>
      >,
      { shouldDirty: true }
    )
  }

  const handleToggleCycleWindow = (
    _: React.ChangeEvent<object>,
    isExpanded: boolean
  ) => {
    setHasCycleWindow(isExpanded)

    if (isExpanded) {
      setValue(
        `items.${index}.start_cycle` as Path<T>,
        Math.max(itemStartCycle, minStartCycle) as PathValue<T, Path<T>>
      )
      return
    }

    // Desligado = cobrado em todos os ciclos disponíveis. Numa recorrência em
    // andamento isso é a partir do piso, não do ciclo 1 — senão a tela diria
    // "todos os ciclos" e o backend silenciosamente moveria para o piso.
    setValue(
      `items.${index}.start_cycle` as Path<T>,
      minStartCycle as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.total_cycles` as Path<T>,
      0 as PathValue<T, Path<T>>
    )
  }

  // Produto com nº de ciclos definido vira o default da janela do item. O backend não
  // deriva mais o limite de product.total_cycles (a janela do item virou a fonte de
  // verdade), então sem esse default um produto de 3 ciclos passaria a ser cobrado para
  // sempre. Abre a seção para o merchant ver o limite — e poder trocar para "Todos".
  const applyProductCycleDefault = (productTotalCycles?: number | null) => {
    if (!isRecurrence) return
    // Não sobrescreve janela já configurada pelo merchant.
    if (itemTotalCycles > 0) return

    const cycles = Number(productTotalCycles || 0)
    if (cycles <= 0) return

    setValue(
      `items.${index}.total_cycles` as Path<T>,
      cycles as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.start_cycle` as Path<T>,
      minStartCycle as PathValue<T, Path<T>>
    )
    setCyclesIsCustom(cycles > 12)
    setHasCycleWindow(true)
    setExpandedAdvanced(true)
  }

  /**
   * Reaplica o default do plano quando a composição VIRA recorrente depois do item já
   * ter sido escolhido.
   *
   * `applyProductCycleDefault` desiste com `!isRecurrence`, e ao escolher um plano de
   * cobrança única (product.total_cycles = 1) o formulário ainda está em "Única" — a
   * janela nunca chega a ser gravada. Ao acrescentar depois um plano mensal, a cobrança
   * passa a ser recorrente e o item ficava com `total_cycles = 0`, que o backend lê como
   * "até o fim da recorrência": uma taxa de instalação virava mensalidade, e a composição
   * exibia "Todos os ciclos" para um item marcado como (1x).
   *
   * Só age sobre item ainda sem janela configurada, então não sobrescreve escolha do
   * merchant nem o que veio do backend na edição.
   */
  useEffect(() => {
    if (!isRecurrence) return
    if (hasCycleWindowConfigured) return

    const product = watch(`items.${index}.product` as Path<T>) as
      | { value?: string; total_cycles?: number }
      | undefined

    if (!product?.value || product.value === 'custom') return

    applyProductCycleDefault(product.total_cycles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecurrence, hasCycleWindowConfigured])

  /**
   * Materializa a CADÊNCIA do produto no item: produto trimestral numa recorrência mensal
   * vira `cycle_interval = 3`. Espelha `BillingItem#apply_product_cycle_interval_default!`
   * — o backend aplica o mesmo default, então isto é só para o merchant ver antes de salvar.
   *
   * O merchant não edita esse valor: ele é consequência da frequência do produto.
   */
  const applyProductCadenceDefault = (product?: {
    frequency?: string
    frequency_type?: string
    frequency_number?: number
    total_cycles?: number
  }) => {
    if (!isRecurrence) return

    const interval = product ? productCycleInterval(product) : 1

    setValue(
      `items.${index}.cycle_interval` as Path<T>,
      (interval ?? 1) as number as PathValue<T, Path<T>>
    )
  }

  const handleChangeStartCycle = (rawValue: string) => {
    const input = rawValue.replace(/[^0-9]/g, '')

    // Campo vazio é estado de digitação legítimo: mantém só no rascunho para o
    // merchant conseguir apagar e redigitar. O form segue com o último valor válido.
    if (input === '') {
      setStartCycleDraft('')
      return
    }

    // Teto do backend (Recurrence model): começar depois do ciclo 120 nunca cobraria.
    // Ignora a tecla que estouraria o limite, como o input de ciclos "Outros".
    if (parseInt(input, 10) > MAX_CYCLES_CHARGE) return

    setStartCycleDraft(input)

    // start_cycle e total_cycles são independentes, como na bonificação:
    // mudar o início não altera a quantidade de ciclos.
    setValue(
      `items.${index}.start_cycle` as Path<T>,
      parseInt(input, 10) as PathValue<T, Path<T>>
    )
  }

  // Sair do campo encerra a digitação: vazio ou abaixo do piso volta para minStartCycle
  // e o rascunho é descartado para o input voltar a espelhar o form.
  const handleBlurStartCycle = () => {
    const parsed = parseInt(startCycleDraft ?? '', 10)

    if (
      startCycleDraft !== null &&
      (Number.isNaN(parsed) || parsed < minStartCycle)
    ) {
      setValue(
        `items.${index}.start_cycle` as Path<T>,
        minStartCycle as PathValue<T, Path<T>>
      )
    }

    // Valor acima do teto só chega aqui vindo do form (item já salvo); a digitação
    // já é barrada no onChange.
    if (itemStartCycle > MAX_CYCLES_CHARGE) {
      setValue(
        `items.${index}.start_cycle` as Path<T>,
        MAX_CYCLES_CHARGE as PathValue<T, Path<T>>
      )
    }

    setStartCycleDraft(null)
  }

  const handleChangeCyclesSelect = (value: number) => {
    if (value === 13) {
      // "Outros": mantém total_cycles atual e revela o input livre
      setCyclesIsCustom(true)
      return
    }
    setCyclesIsCustom(false)
    setValue(
      `items.${index}.total_cycles` as Path<T>,
      value as PathValue<T, Path<T>>
    )
  }

  const handleChangeCyclesCustom = (rawValue: string) => {
    const res = onlyNumber(rawValue)
    if (Number(res) <= MAX_CYCLES_CHARGE) {
      setValue(
        `items.${index}.total_cycles` as Path<T>,
        (Number(res) || 0) as PathValue<T, Path<T>>
      )
    }
  }

  const cycleWindowHelper = () => {
    if (itemStartCycle < minStartCycle) return ''

    // Com cadência, a janela deixa de ser contígua: total_cycles conta COBRANÇAS e o
    // último ciclo é start + (total - 1) * interval.
    const every =
      itemCycleInterval > 1 ? `, a cada ${itemCycleInterval} ciclos` : ''

    if (itemTotalCycles <= 0) {
      return `Cobrado do ciclo ${itemStartCycle} até o fim da recorrência${every}.`
    }

    if (itemTotalCycles === 1) {
      return `Cobrado apenas no ciclo ${itemStartCycle}.`
    }

    const endCycle = itemStartCycle + (itemTotalCycles - 1) * itemCycleInterval
    const times =
      itemCycleInterval > 1
        ? `${itemTotalCycles} vezes`
        : `por ${itemTotalCycles} ciclos`
    return `Cobrado ${times}, do ciclo ${itemStartCycle} ao ${endCycle}${every}.`
  }

  /**
   * Frase curta que explica a cadência herdada do plano. Aparece no cabeçalho do item
   * (fora do accordion), porque é informação que o merchant precisa ver sem abrir nada —
   * é o que diferencia "cobrado todo mês" de "cobrado a cada 3 meses".
   */
  const cadenceHelper = () => {
    if (!isRecurrence || itemCycleInterval <= 1) return ''
    return `Cobrado a cada ${itemCycleInterval} ciclos, seguindo a frequência do plano.`
  }

  // Par canônico da RECORRÊNCIA — a âncora da compatibilidade. Antes a trava comparava com a
  // frequência do primeiro produto recorrente da composição; agora compara com a cadência da
  // própria recorrência, que é quem define o calendário onde os itens se penduram.
  const recurrenceFrequency = watch('frequency' as Path<T>) as
    | number
    | undefined
  const recurrenceFrequencyType = watch('frequency_type' as Path<T>) as
    | FrequencyUnit
    | undefined
  const recurrenceFrequencyNumber = watch('frequency_number' as Path<T>) as
    | number
    | undefined

  const recurrencePair = useMemo<[FrequencyUnit, number]>(() => {
    const name =
      FREQUENCY_ENUM_TO_NAME[Number(recurrenceFrequency ?? 1)] ?? 'monthly'
    return presetToPair(
      name,
      recurrenceFrequencyType,
      Number(recurrenceFrequencyNumber) || undefined
    )
  }, [recurrenceFrequency, recurrenceFrequencyType, recurrenceFrequencyNumber])

  /**
   * Cadência do produto dentro desta recorrência: de quantos em quantos ciclos o item é
   * cobrado. `null` = incompatível (a razão não é inteira), e aí a opção é bloqueada.
   * Espelha `Recurrence#cycle_interval_for` no backend.
   */
  const productCycleInterval = (product?: {
    frequency?: string
    frequency_type?: string
    frequency_number?: number
    total_cycles?: number
  }): number | null => {
    if (!product?.frequency) return 1

    // Plano de frequência personalizada SEM o par: não dá para saber a cadência dele.
    // Supor "mensal" aqui é o que fazia um plano de 45 dias passar pela trava e virar passo
    // 1 — o front dizia compatível e o backend, que lê as colunas de verdade, devolveria 400.
    // Na dúvida, incompatível.
    if (product.frequency === 'custom' && !product.frequency_type) return null

    const productPair = presetToPair(
      product.frequency,
      product.frequency_type as FrequencyUnit | undefined,
      Number(product.frequency_number) || undefined
    )

    return cycleIntervalBetween(recurrencePair, productPair)
  }

  /**
   * O passo do item depende da frequência da recorrência, e ela muda: por derivação (ao
   * escolher outro plano) ou à mão. Um plano semestral é passo 6 numa recorrência mensal e
   * passo 1 numa semestral — sem recalcular, o passo antigo fica e o item é cobrado a cada
   * 6 SEMESTRES, ou seja, de 3 em 3 anos.
   *
   * Roda também na primeira derivação: o ItemRow calcula o passo assim que o plano é
   * escolhido, quando a frequência ainda é o fallback mensal, e a derivação só chega depois.
   */
  useEffect(() => {
    if (!isRecurrence) return

    const product = watch(`items.${index}.product` as Path<T>) as any
    if (!product?.value || product.value === 'custom') return

    const atual = Number(watch(`items.${index}.cycle_interval` as Path<T>) ?? 1)
    const novo = productCycleInterval(product) ?? 1

    if (novo !== atual) {
      setValue(
        `items.${index}.cycle_interval` as Path<T>,
        novo as PathValue<T, Path<T>>
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurrencePair[0], recurrencePair[1], isRecurrence, index])

  /**
   * Por que este plano não entra. Referencia a FREQUÊNCIA DA RECORRÊNCIA, não um item
   * qualquer da composição: é ela que o merchant vê no campo acima e é contra ela que o
   * passo do item é calculado. Apontar "não coincide com o Plano Semestral" confundia
   * quando a recorrência já tinha virado mensal por causa de outro item.
   */
  const incompatibilityReason = (product?: {
    frequency?: string
    frequency_type?: string
    frequency_number?: number
  }): string => {
    if (!product) return ''
    if (product.frequency === 'custom' && !product.frequency_type)
      return 'Frequência personalizada sem intervalo definido.'

    const atual = describeFrequency(
      pairToPreset(recurrencePair[0], recurrencePair[1]) ?? 'custom',
      recurrencePair[0],
      recurrencePair[1]
    )

    return atual
      ? `As datas não coincidem com a frequência ${atual}.`
      : 'As datas não coincidem com a frequência da recorrência.'
  }

  /** Cadência de um plano, no formato que os helpers de derivação esperam. */
  const toCadence = (
    key: string,
    label: string,
    product: {
      frequency?: string
      frequency_type?: string
      frequency_number?: number
    }
  ): ItemCadence => ({
    key,
    label,
    pair: presetToPair(
      product.frequency as string,
      product.frequency_type as FrequencyUnit | undefined,
      Number(product.frequency_number) || undefined
    )
  })

  /** Planos JÁ escolhidos na composição (fora este item), que impõem cadência. */
  const composicaoAtual = (): ItemCadence[] =>
    itemsFields
      .map((_, i) => {
        if (i === index) return null
        const p = watch(`items.${i}.product` as Path<T>) as any
        if (!p?.value || p.value === 'custom' || !p.frequency) return null
        // Plano de cobrança única não impõe cadência.
        if (p.total_cycles === 1 || p.total_cycles === undefined) return null
        return toCadence(p.value, p.label || '', p)
      })
      .filter(Boolean) as ItemCadence[]

  /**
   * A pergunta NÃO é "este plano cabe na frequência atual" — é "este plano cabe com os
   * outros já escolhidos". A diferença importa: escolher semestral e depois mensal precisa
   * funcionar, com a recorrência passando a ser mensal. Comparar com a frequência vigente
   * rejeitaria o mensal justamente por ele ser mais frequente.
   *
   * Produto de cobrança única (`total_cycles === 1`) nunca é bloqueado — não tem cadência.
   * Fora de recorrência não há ciclos, então nada é bloqueado.
   */
  const hasIncompatibleCadence = (product?: {
    frequency?: string
    frequency_type?: string
    frequency_number?: number
    total_cycles?: number
    uuid?: string
    name?: string
  }): boolean => {
    if (!isRecurrence) return false
    if (!product?.frequency) return false
    if (product.total_cycles === 1 || product.total_cycles === undefined)
      return false

    // Plano personalizado sem o par: cadência desconhecida não é cadência mensal.
    if (product.frequency === 'custom' && !product.frequency_type) return true

    return !fitsInComposition(
      composicaoAtual(),
      toCadence(product.uuid || '', product.name || '', product)
    )
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleRemoveProduct = () => {
    // Lido antes de limpar: as tags do item são as que vieram do plano (por herança em
    // applyProductTags/configurePlan), e servem para saber o que tirar das tags gerais.
    const inheritedTags = (watch(`items.${index}.tags` as Path<T>) ||
      []) as ChipTagData[]

    // Remover o produto
    setValue(
      `items.${index}.product` as Path<T>,
      undefined as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.description` as Path<T>,
      '' as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.typedValue` as Path<T>,
      '' as PathValue<T, Path<T>>
    )
    setAutocompleteInputValue('')

    // Zerar o valor, quantidade e total
    setValue(`items.${index}.price` as Path<T>, 0 as PathValue<T, Path<T>>)
    setValue(`items.${index}.quantity` as Path<T>, 1 as PathValue<T, Path<T>>)
    setValue(`items.${index}.total` as Path<T>, 0 as PathValue<T, Path<T>>)

    // Tags também saem: as do plano entram por herança (applyProductTags) e, sem o
    // produto que as trouxe, ficariam órfãs num item em branco. Este caminho é o do
    // item único — com vários, a linha inteira é removida do array e não sobra estado.
    setValue(`items.${index}.tags` as Path<T>, [] as PathValue<T, Path<T>>)

    // O plano também grava as tags nas gerais da cobrança (configurePlan), então elas
    // sobreviveriam à remoção. Saem só as que vieram por herança: uma tag que o
    // merchant tenha adicionado à mão não tem relação com o produto e deve ficar.
    if (inheritedTags.length) {
      const generalTags = (watch('tags' as Path<T>) || []) as ChipTagData[]
      setValue(
        'tags' as Path<T>,
        generalTags.filter(
          (tag) => !inheritedTags.some((inherited) => inherited.id === tag.id)
        ) as PathValue<T, Path<T>>
      )
    }

    // Janela e cadência voltam ao default. Sem isto o total_cycles do plano anterior
    // sobrevivia no item vazio e, pior, fazia applyProductCycleDefault desistir na
    // guarda "não sobrescreve janela configurada": o próximo plano — mesmo sem fim —
    // herdava a janela antiga em vez da sua.
    setValue(
      `items.${index}.start_cycle` as Path<T>,
      minStartCycle as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.total_cycles` as Path<T>,
      0 as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.cycle_interval` as Path<T>,
      1 as PathValue<T, Path<T>>
    )
    setHasCycleWindow(false)
    setCyclesIsCustom(false)

    // Bonificação idem: é configuração do item, não do formulário, e some com ele.
    setValue(
      `items.${index}.bonus_policy` as Path<T>,
      'no_bonus' as PathValue<T, Path<T>>
    )
    setValue(
      `items.${index}.bonus_amount` as Path<T>,
      0 as PathValue<T, Path<T>>
    )
    resetItemBonusWindow()
    setHasItemBonus(false)

    // Item vazio não tem o que configurar: a seção volta fechada, como num item novo.
    setExpandedAdvanced(false)

    // Se for payment link, também resetar o checkbox
    if (isPaymentLink) {
      setValue(
        `items.${index}.enable_customer_select_quantity` as Path<T>,
        false as PathValue<T, Path<T>>
      )
    }

    // Atualizar a descrição com os itens
    setTimeout(() => {
      updateDescriptionWithItems()
    }, 0)

    handleCloseMenu()
  }

  const handlePlanCreated = async (newProductData: any) => {
    // Recarregar os produtos
    await refetchProducts()

    // Fechar o modal
    setIsPlanModalOpen(false)

    // Aguardar um pouco para o refetch ser concluído
    setTimeout(async () => {
      // Buscar os dados completos do produto criado
      const response = await DataService({
        type: 'GET',
        url: `/v2/products/${newProductData.uuid}/form`
      })

      if (response.status === 200) {
        const product = constructorProducts([response.data.record])[0]

        // Setar o produto no form com validação
        setValue(
          `items.${index}.product` as Path<T>,
          {
            label: product.name,
            value: product.uuid,
            total_cycles: product.totalCycles,
            frequency: product.frequency,
            // O par canônico precisa ficar no form: a derivação da frequência da
            // recorrência lê daqui, e sem ele um plano `custom` vira mensal.
            frequency_type: product.frequency_type,
            frequency_number: product.frequency_number
          } as PathValue<T, Path<T>>,
          { shouldValidate: true }
        )
        applyProductCycleDefault(product.totalCycles)
        applyProductCadenceDefault({
          frequency: product.frequency,
          frequency_type: product.frequency_type,
          frequency_number: product.frequency_number,
          total_cycles: product.totalCycles
        })
        setValue(
          `items.${index}.price` as Path<T>,
          product.amount as PathValue<T, Path<T>>,
          { shouldValidate: true }
        )
        setValue(
          `items.${index}.description` as Path<T>,
          '' as PathValue<T, Path<T>>,
          { shouldValidate: true }
        )

        // Setar quantidade se ainda não tiver
        const currentQuantity = watch(`items.${index}.quantity` as Path<T>)
        if (!currentQuantity || currentQuantity === 0) {
          setValue(
            `items.${index}.quantity` as Path<T>,
            1 as PathValue<T, Path<T>>,
            { shouldValidate: true }
          )
        }

        // Atualizar a descrição com os itens
        setTimeout(() => {
          updateDescriptionWithItems()
        }, 0)
      }
    }, 500)
  }

  // Tags do plano no item: o autocomplete (/v1/products_autocomplete) não devolve
  // `tags`, então buscamos o produto para herdá-las. As tags também continuam indo
  // para as tags gerais da cobrança (ver configurePlan), por isso aqui só cuidamos
  // do escopo do item — merge sem duplicar, preservando o que o merchant já pôs.
  const applyProductTags = async (uuid: string) => {
    if (!uuid || uuid === 'custom') return

    try {
      const response = await DataService({
        type: 'GET',
        url: `/v2/products/${uuid}/form`
      })

      if (response.status !== 200) return

      const productTags = (constructorProducts([response.data.record])[0]
        ?.tags || []) as ChipTagData[]
      if (!productTags.length) return

      const currentTags = (watch(`items.${index}.tags` as Path<T>) ||
        []) as ChipTagData[]
      const newTags = productTags.filter(
        (tag) => !currentTags.some((current) => current.id === tag.id)
      )
      if (!newTags.length) return

      setValue(
        `items.${index}.tags` as Path<T>,
        [...currentTags, ...newTags] as PathValue<T, Path<T>>
      )
    } catch {
      // Falha ao herdar tags não deve travar a seleção do produto.
    }
  }

  const getProductToEdit = async (uuid: string) => {
    setIsLoadingProduct(true)
    try {
      const response = await DataService({
        type: 'GET',
        url: `/v2/products/${uuid}/form`
      })

      if (response.status === 200) {
        setProductToBeEdited(constructorProducts([response.data.record])[0])
      }
    } finally {
      setIsLoadingProduct(false)
    }
  }

  const handleEditProduct = async () => {
    const productUuid = watch(`items.${index}.product` as Path<T>)?.value
    if (productUuid) {
      // Fechar o popover primeiro para evitar o "pulo"
      handleCloseMenu()

      // Aguardar um pouco para o popover fechar completamente
      setTimeout(async () => {
        // Ativar o loader no card durante a busca dos dados para edição
        setIsUpdatingProduct(true)
        await getProductToEdit(productUuid)

        // Usar setTimeout para garantir que productToBeEdited esteja atualizado
        setTimeout(() => {
          setIsPlanModalOpen(true)
          // Desativar o loader quando o modal abrir
          setIsUpdatingProduct(false)
        }, 100)
      }, 50)
    }
  }

  // CORREÇÃO: Criar dependências explícitas dos produtos selecionados para garantir atualização
  const selectedProductsInItems = useMemo(() => {
    return itemsFields
      .map(
        (_, itemIndex) => watch(`items.${itemIndex}.product` as Path<T>)?.value
      )
      .filter(Boolean)
  }, [
    itemsFields,
    // Adicionar dependências explícitas para cada item
    ...itemsFields.map(
      (_, itemIndex) => watch(`items.${itemIndex}.product` as Path<T>)?.value
    )
  ])

  useEffect(() => {
    const selectedProductValue = watch(
      `items.${index}.product` as Path<T>
    )?.value
    const foundProduct = products.find(
      (p) => p.value === selectedProductValue
    )?.product

    // Se um produto foi selecionado mas ainda não foi encontrado na lista, está carregando
    if (
      selectedProductValue &&
      selectedProductValue !== 'custom' &&
      !foundProduct
    ) {
      setIsLoadingProduct(true)
    } else {
      setIsLoadingProduct(false)
      setCurrentProduct(foundProduct)
    }
  }, [products, watch(`items.${index}.product` as Path<T>)])

  // Efeito para atualizar o produto quando o shouldRefetch mudar
  useEffect(() => {
    if (shouldRefetch && currentProduct) {
      // Ativar o loader durante a atualização dos dados
      setIsUpdatingProduct(true)

      // Atualizar o produto no formulário com os novos dados
      const updatedProduct = products.find(
        (p) => p.value === currentProduct.uuid
      )?.product
      if (updatedProduct) {
        setValue(
          `items.${index}.product` as Path<T>,
          {
            label: updatedProduct.name,
            value: updatedProduct.uuid,
            total_cycles: updatedProduct.total_cycles,
            frequency: updatedProduct.frequency,
            frequency_type: (updatedProduct as any).frequency_type,
            frequency_number: (updatedProduct as any).frequency_number
          } as PathValue<T, Path<T>>
        )
        setValue(
          `items.${index}.price` as Path<T>,
          updatedProduct.amount as PathValue<T, Path<T>>
        )
      }

      // Desativar o loader após a atualização (independente se encontrou o produto ou não)
      setTimeout(() => {
        setIsUpdatingProduct(false)
      }, 300)
    }
  }, [shouldRefetch, products])

  return (
    <>
      {/* Composição costuma ter vários itens: wrapper enxuto (borda + padding
          reduzidos) para a lista caber sem tanto scroll. */}
      <Stack
        gap={1}
        sx={{
          border: `1px solid ${theme.palette.neutral[700]}`,
          borderRadius: '8px',
          padding: '10px 12px'
        }}
      >
        <Box
          key={field.uuid}
          sx={{
            display: 'grid',
            gap: '10px',
            gridTemplateColumns: !isMobile ? '6.5fr 5.5fr' : '1fr',
            // Base, não centro: a coluna da direita é mais alta (label "Valor"/"Qtde."
            // acima do input), então centralizar deixava o card do plano flutuando.
            // Alinhado pela base, card e inputs compartilham a mesma linha.
            alignItems: !isMobile ? 'flex-end' : 'stretch'
          }}
        >
          {hasProductSelected ? (
            // Render product card when a product is selected
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '6px 10px',
                  // Sombra dava peso demais dentro do wrapper já delimitado por borda;
                  // uma borda sutil separa o card sem inflar a altura da linha.
                  border: `1px solid ${theme.palette.neutral[700]}`,
                  alignItems: 'center',
                  position: 'relative',
                  borderRadius: '8px'
                }}
              >
                {isLoadingProduct || isUpdatingProduct ? (
                  <Stack
                    flexDirection="row"
                    gap="8px"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: '100%' }}
                  >
                    <CircularProgress size="22px" />
                  </Stack>
                ) : (
                  <>
                    <Stack
                      flexDirection="row"
                      gap="8px"
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography variant="smBold" fontSize="14px">
                        {
                          products.find(
                            (p) =>
                              p.value ===
                              watch(`items.${index}.product` as Path<T>)?.value
                          )?.product.name
                        }
                      </Typography>

                      {products.find(
                        (p) =>
                          p.value ===
                          watch(`items.${index}.product` as Path<T>)?.value
                      )?.product.total_cycles == 0 ? (
                        <Icon
                          icon="cached"
                          sx={{
                            width: '18px',
                            height: '18px',
                            color: theme.palette.neutral[400]
                          }}
                        />
                      ) : (
                        <Typography
                          variant="smLight"
                          fontSize="14px"
                          color={theme.palette.neutral[400]}
                        >
                          (
                          {
                            products.find(
                              (p) =>
                                p.value ===
                                watch(`items.${index}.product` as Path<T>)
                                  ?.value
                            )?.product.total_cycles
                          }
                          x)
                        </Typography>
                      )}

                      {/* Tags do item ao lado do nome/ciclos: quem lê o card vê de
                          imediato como aquela linha é classificada e pode remover
                          a tag ali mesmo, sem abrir o popover do bookmark. */}
                      {!!itemTags?.length && (
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          gap={0.5}
                          alignItems="center"
                        >
                          {itemTags.map((tag) => (
                            <ChipTag
                              key={tag.id}
                              tag={tag}
                              showDeleteButton
                              deleteContext="form"
                              onDelete={handleRemoveItemTag}
                              sx={COMPACT_CHIP_TAG_SX}
                            />
                          ))}
                        </Stack>
                      )}
                    </Stack>

                    <IconButton
                      onClick={handleOpenMenu}
                      sx={{ width: '22px', height: '22px' }}
                      disabled={isLoadingProduct || isUpdatingProduct}
                    >
                      <Icon
                        icon="moreHoriz"
                        sx={{ color: theme.palette.neutral[200] }}
                      />
                    </IconButton>
                  </>
                )}
              </Box>

              <Popover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {canManageProducts && (
                  <MenuItem
                    sx={{ borderRadius: '6px' }}
                    onClick={handleEditProduct}
                  >
                    <ListItemIcon>
                      <Icon icon="edit" />
                    </ListItemIcon>
                    <Typography variant="smLight" fontSize="14px">
                      Editar
                    </Typography>
                  </MenuItem>
                )}
                <MenuItem
                  sx={{ borderRadius: '6px' }}
                  onClick={handleRemoveProduct}
                >
                  <ListItemIcon>
                    <Icon icon="close" />
                  </ListItemIcon>
                  <Typography variant="smLight" fontSize="14px">
                    Remover
                  </Typography>
                </MenuItem>
              </Popover>
            </Box>
          ) : (
            // Regular autocomplete for custom items or when no product is selected
            <Box data-cy={`composition-product-${index}`}>
              <Autocomplete
                id={`product-autocomplete-${index}`}
                onChange={(_: React.SyntheticEvent, value: unknown) => {
                  const typedValue = value as {
                    label: string
                    value: string
                    product: ProductAutocomplete
                  }

                  // Check if user clicked the "Add Plan" button
                  if (typedValue?.value === ADD_PLAN_VALUE) {
                    setProductToBeEdited(undefined)
                    setIsPlanModalOpen(true)
                    return
                  }

                  if (!typedValue) {
                    // Limpar todos os campos quando o valor for nulo (usuário limpou o campo)
                    setValue(
                      `items.${index}.description` as Path<T>,
                      '' as PathValue<T, Path<T>>
                    )
                    setValue(
                      `items.${index}.product` as Path<T>,
                      undefined as PathValue<T, Path<T>>
                    )
                    setValue(
                      `items.${index}.typedValue` as Path<T>,
                      '' as PathValue<T, Path<T>>
                    )
                    setTimeout(() => {
                      updateDescriptionWithItems()
                    }, 0)
                    return
                  }

                  if (typedValue.value === 'custom') {
                    setValue(
                      `items.${index}.description` as Path<T>,
                      watch(
                        `items.${index}.typedValue` as Path<T>
                      ) as PathValue<T, Path<T>>
                    )
                    setValue(
                      `items.${index}.product` as Path<T>,
                      undefined as PathValue<T, Path<T>>
                    )
                  } else {
                    // Definir loading como true quando um produto é selecionado
                    setIsLoadingProduct(true)
                    setValue(
                      `items.${index}.product` as Path<T>,
                      {
                        label: typedValue.product.name,
                        value: typedValue.product.uuid,
                        total_cycles: typedValue.product.total_cycles,
                        frequency: typedValue.product.frequency,
                        frequency_type: (typedValue.product as any)
                          .frequency_type,
                        frequency_number: (typedValue.product as any)
                          .frequency_number
                      } as PathValue<T, Path<T>>
                    )
                    applyProductCycleDefault(typedValue.product.total_cycles)
                    applyProductCadenceDefault(typedValue.product)
                    setValue(
                      `items.${index}.price` as Path<T>,
                      typedValue.product.amount as PathValue<T, Path<T>>
                    )
                    setValue(
                      `items.${index}.description` as Path<T>,
                      '' as PathValue<T, Path<T>>
                    )
                    applyProductTags(typedValue.product.uuid)
                  }

                  setTimeout(() => {
                    updateDescriptionWithItems()
                  }, 0)
                }}
                value={
                  watch(`items.${index}.product` as Path<T>) ||
                  (watch(`items.${index}.description` as Path<T>) && {
                    label: watch(`items.${index}.typedValue` as Path<T>), // Use typedValue for dynamic updates
                    value: 'custom'
                  }) ||
                  null
                }
                onBlur={() => {
                  if (
                    !watch(`items.${index}.typedValue` as Path<T>) ||
                    (!!watch(`items.${index}.product` as Path<T>)?.value &&
                      products.filter(
                        (product) =>
                          product?.product?.name ==
                          watch(`items.${index}.typedValue` as Path<T>)
                      ).length > 0)
                  )
                    return

                  setValue(
                    `items.${index}.description` as Path<T>,
                    watch(`items.${index}.typedValue` as Path<T>) as PathValue<
                      T,
                      Path<T>
                    >
                  )
                  setValue(
                    `items.${index}.product` as Path<T>,
                    undefined as PathValue<T, Path<T>>
                  )
                }}
                options={optionsWithAddButton || []}
                autoHighlight
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue.trim()) {
                    return options
                  }
                  const matchingProducts = options.filter(
                    (option) =>
                      (option as any).value !== 'custom' &&
                      (option as any).value !== ADD_PLAN_VALUE &&
                      ((option as any).product?.name || '')
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                  )
                  const customOption = {
                    label: `${inputValue}`,
                    value: 'custom',
                    product: { name: inputValue, uuid: 'custom' }
                  }

                  // Always include the add plan button and matching products
                  const addPlanOption = options.find(
                    (opt) => (opt as any).value === ADD_PLAN_VALUE
                  )
                  const result = []
                  if (addPlanOption) {
                    result.push(addPlanOption)
                  }
                  result.push(...matchingProducts)
                  if (matchingProducts.length > 0) {
                    result.push(customOption)
                  } else {
                    result.push(customOption)
                  }

                  return result
                }}
                autoSelect={false}
                selectOnFocus={false}
                freeSolo={true}
                inputValue={autocompleteInputValue}
                getOptionLabel={(option) => {
                  const optionTyped = option as any

                  if (!optionTyped) return ''

                  // Se for a opção de adicionar plano, retorna vazio
                  if (optionTyped.value === ADD_PLAN_VALUE) return ''

                  // Se for um item avulso, usa a descrição
                  if (optionTyped.value === 'custom') {
                    return (
                      optionTyped.label ||
                      watch(`items.${index}.description` as Path<T>) ||
                      ''
                    )
                  }

                  // Se for um item novo (não tem `product`), retorna o label direto
                  if (!optionTyped.product) {
                    return optionTyped.label || ''
                  }

                  // Caso padrão: exibe o nome do produto
                  return optionTyped.product.name || ''
                }}
                isOptionEqualToValue={(option, value) =>
                  (option as any).value === (value as any).value
                }
                getOptionDisabled={(option) => {
                  const opt = option as any

                  // Never disable the add plan button
                  if (opt.value === ADD_PLAN_VALUE) {
                    return false
                  }

                  // Disable loading option
                  if (opt.value === LOADING_VALUE) {
                    return true
                  }

                  if (opt.value === 'custom') {
                    return false
                  }

                  // Se o produto já foi selecionado, desabilitar
                  if (selectedProductsInItems.includes(opt.value)) {
                    return true
                  }

                  // Cadência incompatível com a recorrência (ver hasIncompatibleCadence).
                  // Produto de cobrança única (total_cycles === 1) nunca é bloqueado.
                  return hasIncompatibleCadence(opt.product)
                }}
                loading={!hasFetched}
                noOptionsText={
                  hasFetched ? 'Não há produtos disponíveis.' : 'Carregando...'
                }
                onInputChange={(_, newInputValue) => {
                  setAutocompleteInputValue(newInputValue)
                  setValue(
                    `items.${index}.typedValue` as Path<T>,
                    newInputValue as PathValue<T, Path<T>>
                  )
                }}
                groupBy={(option) => {
                  const opt = option as any
                  if (opt.value === ADD_PLAN_VALUE) return 'Ações'
                  if (opt.value === LOADING_VALUE) return 'Products'
                  if (opt.value === 'custom') return 'Custom'
                  return 'Products'
                }}
                ListboxProps={
                  {
                    // A lista já montada não reconciliava com o resultado do
                    // filtro — reabrir o popup corrigia. A key força só o
                    // listbox a remontar quando o termo muda; remontar o
                    // Autocomplete inteiro tiraria o foco do input.
                    key: `lb-${index}-${autocompleteInputValue}`
                  } as any
                }
                renderGroup={(params) => {
                  return (
                    <div key={params.key}>
                      {params.group === 'Ações' && <div>{params.children}</div>}
                      {params.group === 'Custom' && (
                        <div>{params.children}</div>
                      )}
                      {params.group === 'Products' && (
                        <div>{params.children}</div>
                      )}
                    </div>
                  )
                }}
                renderOption={(props, option) => {
                  const optionTyped = option as any
                  const product = optionTyped?.product

                  // Render "Add Plan" button
                  if (optionTyped.value === ADD_PLAN_VALUE) {
                    return (
                      <Box
                        component="li"
                        key="add-new-plan"
                        {...props}
                        onClick={(e) => {
                          e.stopPropagation()
                          setProductToBeEdited(undefined)
                          setIsPlanModalOpen(true)
                        }}
                        sx={{
                          width: '100%',
                          borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
                          mt: '-8px'
                        }}
                      >
                        <Stack width="100%" mt={1.2}>
                          <Button
                            fullWidth
                            variantButton="ghost"
                            sx={{ justifyContent: 'flex-start' }}
                          >
                            <Stack
                              direction="row"
                              gap="5px"
                              alignItems="center"
                            >
                              <Icon
                                icon="add"
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontSize: '18px'
                                }}
                              />
                              <Typography
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontWeight: '600',
                                  fontSize: '14px'
                                }}
                              >
                                Adicionar plano
                              </Typography>
                            </Stack>
                          </Button>
                        </Stack>
                      </Box>
                    )
                  }

                  // Render loading option
                  if (optionTyped.value === LOADING_VALUE) {
                    return (
                      <Box
                        component="li"
                        key="loading-products"
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )
                  }

                  if (optionTyped.value === 'custom') {
                    if (watch(`items.${index}.typedValue` as Path<T>) === '')
                      return null

                    return (
                      <MenuItem
                        key={product?.uuid}
                        value={product?.uuid}
                        {...props}
                        sx={{ display: 'flex', gap: 2 }}
                      >
                        <Box
                          width="100%"
                          display="flex"
                          justifyContent="space-between"
                          p="12px"
                          alignItems="center"
                        >
                          <Box display="flex" alignItems="center" gap="12px">
                            <Box
                              bgcolor="#F8F8F8"
                              p={1}
                              borderRadius={1}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <GradientOpenWithIcon
                                icon={'add'}
                                width="32px"
                                height="32px"
                                colors={['#00EC89', '#00F4F5', '#00FEFF']}
                              />
                            </Box>
                            <Box>
                              <Typography>
                                Adicionar{' '}
                                <Typography variant="smBold">
                                  {watch(
                                    `items.${index}.typedValue` as Path<T>
                                  )}
                                </Typography>{' '}
                                como item avulso
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </MenuItem>
                    )
                  }

                  const hasDifferentFrequency = hasIncompatibleCadence(product)

                  // Helper function to format frequency display
                  const getFrequencyDisplay = (
                    product: ProductAutocomplete
                  ) => {
                    if (!product) return ''

                    if (product.total_cycles === 1) {
                      return 'Única (1x)'
                    }

                    // Personalizada: mostra o intervalo ("A cada 45 dias")
                    // em vez do 'custom' cru
                    const frequencyName =
                      (product.frequency === 'custom'
                        ? describeFrequency(
                            product.frequency,
                            product.frequency_type,
                            Number(product.frequency_number) || undefined
                          )
                        : '') ||
                      frequencyEnum[
                        product.frequency as keyof typeof frequencyEnum
                      ] ||
                      product.frequency

                    if (product.total_cycles === 0) {
                      return `${frequencyName} (Indeterminado)`
                    }

                    return `${frequencyName} (${product.total_cycles}x)`
                  }

                  return (
                    <MenuItem
                      key={product?.uuid}
                      value={product?.uuid}
                      {...props}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        ...(hasDifferentFrequency
                          ? {
                              opacity: 0.5,
                              pointerEvents: 'none'
                            }
                          : {})
                      }}
                    >
                      <Box
                        width="100%"
                        display="flex"
                        justifyContent="space-between"
                        p="12px"
                        alignItems="center"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="12px"
                          // Sem isto o bloco cresce com o texto do vencimento e
                          // empurra a frequência para fora do menu.
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          {product?.document_file?.thumbnail_url ? (
                            <Box
                              sx={{
                                display: 'grid',
                                placeContent: 'center',
                                boxSizing: 'border-box',
                                width: '52px',
                                height: '52px',
                                background: '#F8F8F8',
                                borderRadius: '12px',
                                padding: '10px'
                              }}
                            >
                              <Image
                                style={{
                                  borderRadius: '8px',
                                  objectFit: 'contain'
                                }}
                                height={44}
                                width={44}
                                src={product?.document_file?.thumbnail_url}
                                alt="imagem do plano"
                              />
                            </Box>
                          ) : (
                            <Box
                              bgcolor="#F8F8F8"
                              p={1}
                              borderRadius={1}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <GradientOpenWithIcon
                                icon={product?.icon as Icons}
                                width="32px"
                                height="32px"
                                colors={['#00EC89', '#00F4F5', '#00FEFF']}
                              />
                            </Box>
                          )}
                          <Stack sx={{ minWidth: 0 }}>
                            <Typography
                              variant="smBold"
                              fontSize={12}
                              sx={{ wordBreak: 'break-word' }}
                            >
                              {product?.name}
                            </Typography>
                            <Typography
                              variant="smLight"
                              color="#828282"
                              fontSize={12}
                            >
                              Valor:{' '}
                              <strong>
                                R$ {currency(product?.amount || '')}
                              </strong>
                            </Typography>
                            {(() => {
                              const due = getPlanDueDisplay(
                                product?.due_type,
                                product?.due_day
                              )
                              return due ? (
                                <Typography
                                  variant="smLight"
                                  color="#828282"
                                  fontSize={12}
                                  sx={{ whiteSpace: 'normal' }}
                                >
                                  {due.label}: <strong> {due.value}</strong>
                                </Typography>
                              ) : null
                            })()}
                          </Stack>
                        </Box>
                        <Box sx={{ flexShrink: 0, pl: 1 }}>
                          <Typography
                            variant="smBold"
                            color="#828282"
                            align="right"
                            fontSize={12}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {getFrequencyDisplay(product)}
                          </Typography>
                          {/* Bloquear sem dizer o motivo deixa o merchant sem saída: ele vê
                              o plano apagado e não sabe se é permissão, se acabou, ou o quê.
                              O texto diz a razão e o que fazer. */}
                          {hasDifferentFrequency && (
                            <Typography
                              variant="xsLight"
                              color={theme.palette.error?.main || '#EF4444'}
                              align="right"
                              fontSize={11}
                              sx={{ maxWidth: 220, whiteSpace: 'normal' }}
                            >
                              {incompatibilityReason(product)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  )
                }}
                renderInput={(params) => (
                  <Textfield
                    label="Item"
                    placeholder="Escreva ou escolha"
                    helperText={
                      (errors?.items?.[index]?.product ||
                        errors?.items?.[index]?.description ||
                        errors?.items?.[index]?.message) as string
                    }
                    error={
                      !!errors?.items?.[index]?.product ||
                      !!errors?.items?.[index]?.description ||
                      !!errors?.items?.[index]?.message
                    }
                    sx={{
                      '& .MuiFormHelperText-root': {
                        height: '0px'
                      }
                    }}
                    {...params}
                  />
                )}
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: '1.5fr 1fr 1fr 40px 40px',
              // Base: os campos com label são mais altos que as células sem label, e
              // todos precisam terminar na mesma linha do "Item" da coluna esquerda.
              alignItems: 'end'
            }}
          >
            <Box data-cy={`composition-value-${index}`}>
              <Controller
                control={control}
                name={`items.${index}.price` as Path<T>}
                render={({ field: { onChange, value, ref } }) => (
                  <Textfield
                    customStartAdornment={<Typography mr="6px">R$</Typography>}
                    inputRef={ref}
                    helperText={
                      errors?.items?.[index]?.price?.message as string
                    }
                    error={!!errors?.items?.[index]?.price}
                    label="Valor"
                    mask="money"
                    name="price"
                    placeholder="Digite o valor"
                    startAdornment={true}
                    onChange={(e) => {
                      onChange(e)

                      setTimeout(() => {
                        updateDescriptionWithItems()
                      }, 0)
                    }}
                    value={currency(value || '0')}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        height: '0px'
                      }
                    }}
                  />
                )}
              />
            </Box>

            <Box data-cy={`composition-quantity-${index}`}>
              <Controller
                control={control}
                name={`items.${index}.quantity` as Path<T>}
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <Textfield
                    label="Qtde."
                    type="text"
                    placeholder="Quantidade"
                    helperText={error?.message}
                    error={!!error?.message}
                    onChange={(e) => {
                      const input = e.target.value.replace(',', '.')
                      // Para payment link, permitir 0. Para outros formulários, forçar mínimo 1
                      if (!isPaymentLink && input === '0') {
                        onChange('1')
                      } else {
                        onChange(input)
                      }

                      setTimeout(() => {
                        updateDescriptionWithItems()
                      }, 0)
                    }}
                    value={value?.toString().replace('.', ',') ?? ''}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        height: '0px'
                      }
                    }}
                  />
                )}
              />
            </Box>

            {/* Total, tags e remover se alinham à altura do input (não ao topo da
                célula, onde ficam os labels). `height: FIELD_ROW_HEIGHT` casa com a
                altura do campo em vez de `pt`/`mt` fixos: assim estas células não
                ficam mais altas que os inputs e a coluna inteira para de esticar —
                era essa altura extra que empurrava o "Item" para baixo, já que a
                linha externa alinha as duas colunas pela base. */}
            <Box
              sx={{
                height: FIELD_ROW_HEIGHT,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Typography>
                {formatMoney(watch(`items.${index}.total` as Path<T>))}
              </Typography>
            </Box>

            {/* Tags do item: mesmo gesto usado nas telas de detalhe
                (bookmark + badge). Fica no cabeçalho do item para que a
                contagem seja visível sem abrir nada. */}
            <Box
              data-cy={`item-tags-${index}`}
              sx={{
                height: FIELD_ROW_HEIGHT,
                display: 'grid',
                placeItems: 'center'
              }}
            >
              <TagsPopover
                control={control}
                name={`items.${index}.tags` as Path<T>}
                maxTags={10}
              />
            </Box>

            <Box
              sx={{
                height: FIELD_ROW_HEIGHT,
                display: 'grid',
                placeItems: 'center'
              }}
            >
              <IconButton
                disabled={
                  itemsFields.length == 1 &&
                  !watch(`items.${index}.product` as Path<T>)?.value &&
                  !watch(`items.${index}.description` as Path<T>)
                }
                onClick={() => {
                  if (itemsFields.length == 1) {
                    handleRemoveProduct()
                    return
                  }

                  if (removeItem) {
                    removeItem(index)
                    setTimeout(() => {
                      updateDescriptionWithItems()
                    }, 0)
                  }
                }}
              >
                <Icon
                  icon="close"
                  colorSX={
                    !(
                      itemsFields.length == 1 &&
                      !watch(`items.${index}.product` as Path<T>)?.value &&
                      !watch(`items.${index}.description` as Path<T>)
                    )
                      ? '#777877'
                      : lighten('#777877', 0.4)
                  }
                />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Espelho das tags para item sem plano (descrição livre): não há card onde
            encaixá-las ao lado do nome, então ficam abaixo da linha. Com plano
            selecionado, os chips vivem dentro do card. */}
        {!!itemTags?.length && !hasProductSelected && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={-0.5}>
            {itemTags.map((tag) => (
              <ChipTag
                key={tag.id}
                tag={tag}
                showDeleteButton
                deleteContext="form"
                onDelete={handleRemoveItemTag}
                sx={COMPACT_CHIP_TAG_SX}
              />
            ))}
          </Stack>
        )}

        {/* Cadência herdada do plano. Não é editável — é consequência da frequência do
            produto — mas precisa estar visível sem abrir accordion nenhum, porque é o que
            distingue "cobrado todo mês" de "cobrado a cada 3 meses". */}
        {!!cadenceHelper() && (
          <Typography variant="xsLight" color={theme.palette.neutral[400]}>
            {cadenceHelper()}
          </Typography>
        )}

        {/* Checkbox específico para Payment Link + Campo de quantidade mínima */}
        {isPaymentLink && (
          <Stack direction="row" alignItems="center" sx={{ mt: -1.5, mb: 0 }}>
            <Controller
              control={control}
              name={`items.${index}.enable_customer_select_quantity` as Path<T>}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value || false}
                      onChange={(e) => {
                        field.onChange(e.target.checked)
                        if (e.target.checked) {
                          // Ao marcar, verificar se a quantidade mínima (padrão 1) é maior que a quantidade atual
                          const currentQuantity = watch(
                            `items.${index}.quantity` as Path<T>
                          )
                          const currentQtyNum =
                            typeof currentQuantity === 'string'
                              ? parseFloat(currentQuantity.replace(',', '.'))
                              : Number(currentQuantity) || 0
                          const minQty =
                            watch(
                              `items.${index}.minimum_quantity` as Path<T>
                            ) || 1
                          if (minQty > currentQtyNum) {
                            setValue(
                              `items.${index}.quantity` as Path<T>,
                              minQty as PathValue<T, Path<T>>
                            )
                            setTimeout(() => {
                              updateDescriptionWithItems()
                            }, 0)
                          }
                        } else {
                          // Se desmarcar, resetar minimum_quantity para 1
                          setValue(
                            `items.${index}.minimum_quantity` as Path<T>,
                            1 as PathValue<T, Path<T>>
                          )
                        }
                      }}
                      size="small"
                    />
                  }
                  label="Permitir que o cliente escolha a quantidade"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: theme.palette.neutral[300]
                    }
                  }}
                />
              )}
            />

            {/* Campo de quantidade mínima - visível apenas quando enable_customer_select_quantity está ativo */}
            {watch(
              `items.${index}.enable_customer_select_quantity` as Path<T>
            ) && (
              <Controller
                control={control}
                name={`items.${index}.minimum_quantity` as Path<T>}
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: theme.palette.neutral[500],
                        mx: 1
                      }}
                    >
                      |
                    </Typography>
                    <Typography
                      variant="smLight"
                      sx={{
                        fontSize: '14px',
                        color: theme.palette.neutral[300],
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Quantidade mínima:
                    </Typography>
                    <Textfield
                      type="text"
                      placeholder="0"
                      helperText={error?.message}
                      error={!!error?.message}
                      onChange={(e) => {
                        const input = e.target.value.replace(/[^0-9]/g, '')
                        const numValue = parseInt(input, 10)
                        // Permitir 0 ou maior
                        if (input === '' || isNaN(numValue)) {
                          onChange(0)
                        } else {
                          onChange(numValue)
                          // Se a quantidade mínima for maior que a quantidade atual, atualizar a quantidade
                          const currentQuantity = watch(
                            `items.${index}.quantity` as Path<T>
                          )
                          const currentQtyNum =
                            typeof currentQuantity === 'string'
                              ? parseFloat(currentQuantity.replace(',', '.'))
                              : Number(currentQuantity) || 0
                          if (numValue > currentQtyNum) {
                            setValue(
                              `items.${index}.quantity` as Path<T>,
                              numValue as PathValue<T, Path<T>>
                            )
                            setTimeout(() => {
                              updateDescriptionWithItems()
                            }, 0)
                          }
                        }
                      }}
                      value={value?.toString() ?? '1'}
                      sx={{
                        width: '80px',
                        '& .MuiFormHelperText-root': {
                          height: '0px'
                        }
                      }}
                    />
                  </Stack>
                )}
              />
            )}
          </Stack>
        )}

        {/* Opções avançadas do item: bonificação (todos os contextos) e janela de
            ciclos (só recorrência). As tags saíram daqui para o cabeçalho. */}
        <Stack width="100%" mt={-0.5}>
          <Stack
            data-cy={`item-advanced-options-toggle-${index}`}
            onClick={() => setExpandedAdvanced(!expandedAdvanced)}
            direction="row"
            gap={0.5}
            alignItems="center"
            mb={expandedAdvanced ? 1.5 : 0}
            sx={{ cursor: 'pointer', alignSelf: 'flex-start' }}
          >
            <Typography
              variant="smBold"
              fontSize="13px"
              color={theme.palette.neutral[300]}
            >
              Opções avançadas
            </Typography>

            <Button
              startIcon={expandedAdvanced ? 'faChevronUp' : 'faChevronDown'}
              variantButton="ghost"
              sx={{
                fontSize: '12px',
                width: '20px',
                height: '20px',
                minHeight: '20px',
                ':hover': {
                  height: '20px'
                }
              }}
            />
          </Stack>

          {/* Carregar o plano grava vários campos em sequência (janela, cadência, preço,
              tags) e cada escrita reavalia os switches — abertos, eles ligavam e
              desligavam à vista. O skeleton cobre essa montagem: o merchant vê o
              estado final, não o caminho até ele. */}
          {expandedAdvanced &&
            (isLoadingProduct || isUpdatingProduct || isSettlingPlan) && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  alignItems: 'start',
                  gap: 1.5
                }}
              >
                {[0, 1].map((key) => (
                  <Skeleton
                    key={key}
                    variant="rounded"
                    height={54}
                    sx={{ borderRadius: '8px' }}
                  />
                ))}
              </Box>
            )}

          {expandedAdvanced &&
            !isLoadingProduct &&
            !isUpdatingProduct &&
            !isSettlingPlan && (
              /* Duas colunas sempre: os dois cards existem em qualquer tipo de cobrança
               (em única o de ciclos aparece desabilitado), então a grade não muda de
               forma ao alternar Única/Recorrente. Abaixo de `md` empilha — lado a lado
               os campos internos ficariam estreitos demais. `alignItems: start` impede
               que o card menor estique junto com o maior quando um está aberto. */
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: '1fr 1fr'
                  },
                  alignItems: 'start',
                  gap: 1.5
                }}
              >
                {/* Bonificação do item — vale em recorrência, avulsa e link */}
                <Accordion
                  data-cy={`item-bonus-accordion-${index}`}
                  expanded={hasItemBonus}
                  onChange={handleToggleItemBonus}
                  sx={ADVANCED_ACCORDION_SX}
                >
                  <AccordionSummary
                    data-cy={`item-bonus-toggle-${index}`}
                    sx={{
                      '.MuiAccordionSummary-content': { margin: '12px 0px' }
                    }}
                    hasSwitch
                    expandIcon={
                      <Switch disabled={!canUseBonus} checked={hasItemBonus} />
                    }
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="smBold">
                        Aplicar bonificação no item
                      </Typography>
                      {!canUseBonus && (
                        <Upgrade
                          resourceName="Bonificação"
                          plan={bonusAccess?.plan}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack gap={1}>
                      {/* Em duas colunas os 190px + 160px não cabem lado a lado: os campos
                        passam a dividir a largura disponível e quebram quando apertados. */}
                      <Stack
                        direction="row"
                        gap={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.5}
                            mb={0.5}
                          >
                            <Typography variant="smLight">Tipo</Typography>
                            <Tooltip
                              enterTouchDelay={0}
                              arrow
                              placement="top"
                              title="Bonificação em valor (R$) ou em percentual (%) sobre o total do item."
                            >
                              <Box
                                component="span"
                                sx={{ cursor: 'help', display: 'flex' }}
                              >
                                <Icon
                                  icon="info"
                                  sx={{
                                    fontSize: '14px',
                                    color: theme.palette.neutral[500]
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </Stack>
                          <Select
                            fullWidth
                            value={
                              itemBonusPolicy === 'no_bonus'
                                ? 'fixed'
                                : itemBonusPolicy
                            }
                            onChange={(event) =>
                              handleChangeBonusPolicy(
                                event.target.value as ItemBonusPolicy
                              )
                            }
                          >
                            {ITEM_BONUS_POLICY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>

                        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                          <Typography
                            variant="smLight"
                            mb={0.5}
                            display="block"
                          >
                            Valor da bonificação
                          </Typography>
                          <Controller
                            control={control}
                            name={`items.${index}.bonus_amount` as Path<T>}
                            render={({
                              field: { onChange, value, ref, name }
                            }) => (
                              <Textfield
                                data-cy={`item-bonus-amount-${index}`}
                                inputRef={ref}
                                name={name}
                                placeholder={
                                  itemBonusPolicy === 'percentage' ? '%' : 'R$'
                                }
                                {...(itemBonusPolicy === 'percentage'
                                  ? {
                                      // Mesmo componente do desconto geral: sufixo "%",
                                      // casas decimais e teto de 100% no próprio input.
                                      InputProps: {
                                        inputComponent:
                                          NumberFormatCustom as any
                                      },
                                      onChange: (e: any) =>
                                        onChange(e.target.value),
                                      value: value == null ? '' : String(value)
                                    }
                                  : {
                                      mask: 'money',
                                      startAdornment: true,
                                      customStartAdornment: (
                                        <Typography mr="6px">R$</Typography>
                                      ),
                                      onChange,
                                      value: currency(value ?? 0)
                                    })}
                              />
                            )}
                          />
                        </Box>
                      </Stack>

                      {/* Janela da bonificação (SC-11169): mesma regra da bonificação
                        geral, nos ciclos da recorrência. Na avulsa não se aplica. */}
                      {isRecurrence && (
                        <Stack
                          direction="row"
                          gap={2}
                          alignItems="flex-start"
                          flexWrap="wrap"
                        >
                          <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                              mb={0.5}
                            >
                              <Typography variant="smLight">
                                A partir de qual ciclo?
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="top"
                                title="A partir de qual ciclo da recorrência a bonificação deste item vale."
                              >
                                <Box
                                  component="span"
                                  sx={{ cursor: 'help', display: 'flex' }}
                                >
                                  <Icon
                                    icon="info"
                                    sx={{
                                      fontSize: '14px',
                                      color: theme.palette.neutral[500]
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            </Stack>
                            <Textfield
                              data-cy={`item-bonus-start-cycle-${index}`}
                              type="number"
                              inputProps={{ min: 1, max: MAX_CYCLES_CHARGE }}
                              placeholder="1"
                              value={
                                bonusStartCycleDraft ??
                                itemBonusStartCycle.toString()
                              }
                              onChange={(e) =>
                                handleChangeBonusStartCycle(e.target.value)
                              }
                              onBlur={handleBlurBonusStartCycle}
                            />
                          </Box>

                          <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                              mb={0.5}
                            >
                              <Typography variant="smLight">
                                Por quantos ciclos?
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="top"
                                title="Por quantos ciclos a bonificação deste item vale. Todos = até o fim da recorrência. Depois disso o item é cobrado cheio."
                              >
                                <Box
                                  component="span"
                                  sx={{ cursor: 'help', display: 'flex' }}
                                >
                                  <Icon
                                    icon="info"
                                    sx={{
                                      fontSize: '14px',
                                      color: theme.palette.neutral[500]
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            </Stack>
                            <Select
                              data-cy={`item-bonus-total-cycles-${index}`}
                              fullWidth
                              value={bonusCyclesSelectValue}
                              onChange={(event) =>
                                handleChangeBonusCyclesSelect(
                                  Number(event.target.value)
                                )
                              }
                            >
                              {ITEM_CYCLES_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          {bonusCyclesSelectValue === 13 && (
                            <Box sx={{ width: '120px' }}>
                              <Typography variant="smLight" mb="6px">
                                Qtd. de ciclos
                              </Typography>
                              <Textfield
                                data-cy={`item-bonus-total-cycles-custom-${index}`}
                                placeholder="Qtd."
                                value={
                                  itemBonusTotalCycles > 0
                                    ? itemBonusTotalCycles.toString()
                                    : ''
                                }
                                onChange={(e) =>
                                  handleChangeBonusCyclesCustom(e.target.value)
                                }
                              />
                            </Box>
                          )}
                        </Stack>
                      )}

                      <Typography
                        variant="smLight"
                        fontSize="12px"
                        color={theme.palette.neutral[400]}
                      >
                        {itemBonusValue > 0
                          ? `Bonificação de ${formatMoney(
                              itemBonusValue
                            )} abatida do valor do item${
                              bonusWindowLabel ? ` ${bonusWindowLabel}` : ''
                            }.${
                              bonusWindowLabel
                                ? ' Nos outros ciclos o item é cobrado cheio.'
                                : ''
                            }`
                          : 'A bonificação é abatida do valor do item em todas as cobranças.'}
                      </Typography>
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Sempre montado, mesmo em cobrança única: alternar Única/Recorrente
                  montava e desmontava o accordion, e a seção inteira saltava de altura.
                  Desabilitado, o layout fica estável e o motivo vira explícito no
                  tooltip em vez de o controle simplesmente sumir. */}
                <Accordion
                  expanded={isRecurrence && hasCycleWindow}
                  onChange={isRecurrence ? handleToggleCycleWindow : undefined}
                  sx={{
                    ...ADVANCED_ACCORDION_SX,
                    ...(!isRecurrence && {
                      borderLeftColor: theme.palette.neutral[600],
                      opacity: 0.6
                    })
                  }}
                >
                  <Tooltip
                    enterTouchDelay={0}
                    arrow
                    placement="top"
                    title={
                      isRecurrence
                        ? ''
                        : 'Só é possível escolher os ciclos em cobranças recorrentes.'
                    }
                  >
                    <AccordionSummary
                      data-cy={`item-cycle-window-toggle-${index}`}
                      sx={{
                        '.MuiAccordionSummary-content': { margin: '12px 0px' },
                        ...(!isRecurrence && { cursor: 'not-allowed' })
                      }}
                      hasSwitch
                      expandIcon={
                        <Switch
                          checked={isRecurrence && hasCycleWindow}
                          disabled={!isRecurrence}
                        />
                      }
                    >
                      <Typography
                        variant="smBold"
                        color={
                          isRecurrence ? undefined : theme.palette.neutral[400]
                        }
                      >
                        Cobrar só em ciclos específicos
                      </Typography>
                    </AccordionSummary>
                  </Tooltip>
                  <AccordionDetails>
                    <Stack gap={1}>
                      <Stack
                        direction="row"
                        gap={2}
                        alignItems="flex-start"
                        flexWrap="wrap"
                      >
                        {/* A partir de qual ciclo? -> start_cycle */}
                        <Box sx={{ flex: '1 1 160px', minWidth: '160px' }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.5}
                            mb={0.5}
                          >
                            <Typography variant="smLight">
                              A partir de qual ciclo?
                            </Typography>
                            <Tooltip
                              enterTouchDelay={0}
                              arrow
                              placement="top"
                              title="A partir de qual ciclo da recorrência este item será cobrado."
                            >
                              <Box
                                component="span"
                                sx={{ cursor: 'help', display: 'flex' }}
                              >
                                <Icon
                                  icon="info"
                                  sx={{
                                    fontSize: '14px',
                                    color: theme.palette.neutral[500]
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </Stack>
                          <Textfield
                            data-cy={`item-start-cycle-${index}`}
                            type="number"
                            inputProps={{
                              min: minStartCycle,
                              max: MAX_CYCLES_CHARGE
                            }}
                            placeholder={minStartCycle.toString()}
                            value={
                              startCycleDraft ??
                              (itemStartCycle ? itemStartCycle.toString() : '')
                            }
                            onChange={(e) =>
                              handleChangeStartCycle(e.target.value)
                            }
                            onBlur={handleBlurStartCycle}
                            error={itemStartCycle < minStartCycle}
                            helperText={
                              itemStartCycle < minStartCycle
                                ? `Mínimo: ${minStartCycle}`
                                : ''
                            }
                          />
                        </Box>

                        {/* Por quantos ciclos? -> total_cycles (0 = até o fim) */}
                        <Box sx={{ flex: '1 1 160px', minWidth: '160px' }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.5}
                            mb={0.5}
                          >
                            <Typography variant="smLight">
                              Por quantos ciclos?
                            </Typography>
                            <Tooltip
                              enterTouchDelay={0}
                              arrow
                              placement="top"
                              title="Por quantos ciclos este item será cobrado. Todos = até o fim da recorrência."
                            >
                              <Box
                                component="span"
                                sx={{ cursor: 'help', display: 'flex' }}
                              >
                                <Icon
                                  icon="info"
                                  sx={{
                                    fontSize: '14px',
                                    color: theme.palette.neutral[500]
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </Stack>
                          <Select
                            data-cy={`item-total-cycles-${index}`}
                            fullWidth
                            value={cyclesSelectValue}
                            onChange={(event) =>
                              handleChangeCyclesSelect(
                                Number(event.target.value)
                              )
                            }
                          >
                            {ITEM_CYCLES_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>

                        {/* Input livre quando "Outros" */}
                        {cyclesSelectValue === 13 && (
                          <Box sx={{ width: '120px' }}>
                            <Typography variant="smLight" mb="6px">
                              Qtd. de ciclos
                            </Typography>
                            <Textfield
                              placeholder="Qtd."
                              value={
                                itemTotalCycles > 0
                                  ? itemTotalCycles.toString()
                                  : ''
                              }
                              onChange={(e) =>
                                handleChangeCyclesCustom(e.target.value)
                              }
                            />
                          </Box>
                        )}
                      </Stack>
                      <Typography
                        variant="smLight"
                        fontSize="12px"
                        color={theme.palette.neutral[400]}
                      >
                        {cycleWindowHelper()}
                      </Typography>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
        </Stack>
      </Stack>

      {isPlanModalOpen && (
        <Modal
          handleClose={() => {
            setIsPlanModalOpen(false)
            setProductToBeEdited(undefined)
          }}
          open={isPlanModalOpen}
          width="100%"
          maxWidth="80vw"
          maxHeight="90vh"
          height="100%"
        >
          <FormProducts
            onSuccessFunction={async () => {
              // Se está editando um produto, ativar o loader
              if (productToBeEdited) {
                setIsUpdatingProduct(true)
                setShouldRefetch(true)
              }

              // Solicitar refetch dos produtos
              await refetchProducts()

              // Fechar o modal
              setIsPlanModalOpen(false)
              setProductToBeEdited(undefined)

              // Se estava editando, atualizar a descrição
              if (productToBeEdited) {
                setTimeout(() => {
                  updateDescriptionWithItems()
                  setShouldRefetch(false)
                }, 500)
              }
            }}
            product={productToBeEdited}
            setIsOpenDialog={() => {
              setIsPlanModalOpen(false)
              setProductToBeEdited(undefined)
            }}
            onProductCreated={async (productUuid: string) => {
              await handlePlanCreated({ uuid: productUuid })
            }}
          />
        </Modal>
      )}
    </>
  )
}

export default ItemRow
